import { createReducer, createAction } from '@reduxjs/toolkit';
import {
  fetchAssignedQueues,
  fetchTickerStats,
  fetchSubviewWorkitems,
  fetchGroupMetrics,
  CoderQueueItem,
  CoderTickerStats,
  CoderGroupMetrics,
  CoderWorkitem,
  SubviewTabKey
} from '../actions/coder-view.action';

export interface SubviewWorkitemState {
  items: CoderWorkitem[];
  total: number;
}

export interface CoderViewIsFetching {
  assignedQueues: boolean;
  tickerStats: boolean;
  subviewWorkitems: boolean;
  groupMetrics: boolean;
}

export interface CoderViewState {
  assignedQueues: CoderQueueItem[];
  tickerStats: CoderTickerStats;
  groupMetrics: CoderGroupMetrics;
  subviewWorkitems: Record<SubviewTabKey, SubviewWorkitemState>;
  isFetching: CoderViewIsFetching;
  activeTab: SubviewTabKey;
  errors: {
    assignedQueues: string | null;
    tickerStats: string | null;
    subviewWorkitems: string | null;
    groupMetrics: string | null;
  };
}

const emptySubview = (): SubviewWorkitemState => ({ items: [], total: 0 });

const initialState: CoderViewState = {
  assignedQueues: [],
  tickerStats: {
    submitted: 0,
    excluded: 0,
    providerHolds: 0,
    managerHolds: 0,
    over30Days: 0
  },
  groupMetrics: {
    casesOnHold: 0,
    escalatedCases: 0,
    completedToday: 0
  },
  subviewWorkitems: {
    assigned: emptySubview(),
    holdsAndRequests: emptySubview(),
    submittedThisMonth: emptySubview()
  },
  isFetching: {
    assignedQueues: false,
    tickerStats: false,
    subviewWorkitems: false,
    groupMetrics: false
  },
  activeTab: 'assigned',
  errors: {
    assignedQueues: null,
    tickerStats: null,
    subviewWorkitems: null,
    groupMetrics: null
  }
};

export const setActiveTab = createAction<SubviewTabKey>(
  'coderView/setActiveTab'
);

const reducer = createReducer(initialState, builder => {
  builder.addCase(fetchAssignedQueues.pending, state => {
    state.isFetching.assignedQueues = true;
    state.errors.assignedQueues = null;
  });
  builder.addCase(fetchAssignedQueues.fulfilled, (state, action) => {
    state.assignedQueues = Array.isArray(action.payload) ? action.payload : [];
    state.isFetching.assignedQueues = false;
  });
  builder.addCase(fetchAssignedQueues.rejected, (state, action) => {
    state.isFetching.assignedQueues = false;
    state.errors.assignedQueues =
      action.error.message ?? 'Failed to load assigned queues';
  });

  builder.addCase(fetchTickerStats.pending, state => {
    state.isFetching.tickerStats = true;
    state.errors.tickerStats = null;
  });
  builder.addCase(fetchTickerStats.fulfilled, (state, action) => {
    state.tickerStats = action.payload;
    state.isFetching.tickerStats = false;
  });
  builder.addCase(fetchTickerStats.rejected, (state, action) => {
    state.isFetching.tickerStats = false;
    state.errors.tickerStats =
      action.error.message ?? 'Failed to load ticker stats';
  });

  builder.addCase(fetchSubviewWorkitems.pending, state => {
    state.isFetching.subviewWorkitems = true;
    state.errors.subviewWorkitems = null;
  });
  builder.addCase(fetchSubviewWorkitems.fulfilled, (state, action) => {
    const { tab, items, total } = action.payload;
    state.subviewWorkitems[tab] = { items, total };
    state.isFetching.subviewWorkitems = false;
  });
  builder.addCase(fetchSubviewWorkitems.rejected, (state, action) => {
    state.isFetching.subviewWorkitems = false;
    state.errors.subviewWorkitems =
      action.error.message ?? 'Failed to load work items';
  });

  builder.addCase(fetchGroupMetrics.pending, state => {
    state.isFetching.groupMetrics = true;
    state.errors.groupMetrics = null;
  });
  builder.addCase(fetchGroupMetrics.fulfilled, (state, action) => {
    state.groupMetrics = action.payload;
    state.isFetching.groupMetrics = false;
  });
  builder.addCase(fetchGroupMetrics.rejected, (state, action) => {
    state.isFetching.groupMetrics = false;
    state.errors.groupMetrics =
      action.error.message ?? 'Failed to load group metrics';
  });

  builder.addCase(setActiveTab, (state, action) => {
    state.activeTab = action.payload;
  });
});

export default reducer;
